import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';
import {
  LinkedInIntegration,
  LinkedInCampaign,
  LinkedInAccount,
  LinkedInStepConfig,
  FailedTaskDetail,
} from './types';
import {
  getLinkedInIntegrationsRequest,
  connectIntegrationRequest,
  rotateApiKeyRequest,
  updateFieldMappingsRequest,
  deleteIntegrationRequest,
  verifyIntegrationRequest,
  reconnectIntegrationRequest,
  getCampaignsRequest,
  getLinkedInAccountsRequest,
  getStepConfigRequest,
  retryLinkedInTasksRequest,
  getFailedLinkedInTasksRequest,
  getLinkedInTourSeenRequest,
  dismissLinkedInTourRequest,
} from './extra-actions';

interface State {
  integrations: LinkedInIntegration[];
  campaigns: Record<number, LinkedInCampaign[]>;
  cachedAt: Record<number, string>;
  linkedInAccounts: Record<number, LinkedInAccount[]>;
  stepConfigs: Record<number, LinkedInStepConfig>;
  failedTasks: FailedTaskDetail[];
  failedTasksTotal: number;
  tourSeen: boolean;
  getIntegrationsStatus: RequestStatus;
  connectIntegrationStatus: RequestStatus;
  rotateApiKeyStatus: RequestStatus;
  updateFieldMappingsStatus: RequestStatus;
  deleteIntegrationStatus: RequestStatus;
  verifyIntegrationStatus: RequestStatus;
  reconnectIntegrationStatus: RequestStatus;
  getCampaignsStatus: Record<number, RequestStatus>;
  getAccountsStatus: Record<number, RequestStatus>;
  getStepConfigStatus: RequestStatus;
  retryTasksStatus: RequestStatus;
  getFailedTasksStatus: RequestStatus;
  error: string | null;
}

const initialState: State = {
  integrations: [],
  campaigns: {},
  cachedAt: {},
  linkedInAccounts: {},
  stepConfigs: {},
  failedTasks: [],
  failedTasksTotal: 0,
  tourSeen: false,
  getIntegrationsStatus: RequestStatus.Ideal,
  connectIntegrationStatus: RequestStatus.Ideal,
  rotateApiKeyStatus: RequestStatus.Ideal,
  updateFieldMappingsStatus: RequestStatus.Ideal,
  deleteIntegrationStatus: RequestStatus.Ideal,
  verifyIntegrationStatus: RequestStatus.Ideal,
  reconnectIntegrationStatus: RequestStatus.Ideal,
  getCampaignsStatus: {},
  getAccountsStatus: {},
  getStepConfigStatus: RequestStatus.Ideal,
  retryTasksStatus: RequestStatus.Ideal,
  getFailedTasksStatus: RequestStatus.Ideal,
  error: null,
};

const linkedInAutomationSlice = createSlice({
  name: 'linkedInAutomation',
  initialState,
  reducers: {
    resetConnectIntegrationStatus: (state) => {
      state.connectIntegrationStatus = RequestStatus.Ideal;
    },
    resetRotateApiKeyStatus: (state) => {
      state.rotateApiKeyStatus = RequestStatus.Ideal;
    },
    resetUpdateFieldMappingsStatus: (state) => {
      state.updateFieldMappingsStatus = RequestStatus.Ideal;
    },
    resetDeleteIntegrationStatus: (state) => {
      state.deleteIntegrationStatus = RequestStatus.Ideal;
    },
    resetVerifyIntegrationStatus: (state) => {
      state.verifyIntegrationStatus = RequestStatus.Ideal;
    },
    resetReconnectIntegrationStatus: (state) => {
      state.reconnectIntegrationStatus = RequestStatus.Ideal;
    },
    resetRetryTasksStatus: (state) => {
      state.retryTasksStatus = RequestStatus.Ideal;
    },
    setTourSeen: (state, action) => {
      state.tourSeen = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Integrations
    builder.addCase(getLinkedInIntegrationsRequest.pending, (state) => {
      state.getIntegrationsStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(getLinkedInIntegrationsRequest.fulfilled, (state, action) => {
      state.getIntegrationsStatus = RequestStatus.Succeeded;
      state.integrations = action.payload;
    });
    builder.addCase(getLinkedInIntegrationsRequest.rejected, (state, action) => {
      state.getIntegrationsStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Connect Integration
    builder.addCase(connectIntegrationRequest.pending, (state) => {
      state.connectIntegrationStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(connectIntegrationRequest.fulfilled, (state, action) => {
      state.connectIntegrationStatus = RequestStatus.Succeeded;
      state.integrations = [...state.integrations, action.payload];
    });
    builder.addCase(connectIntegrationRequest.rejected, (state, action) => {
      state.connectIntegrationStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Rotate API Key
    builder.addCase(rotateApiKeyRequest.pending, (state) => {
      state.rotateApiKeyStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(rotateApiKeyRequest.fulfilled, (state, action) => {
      state.rotateApiKeyStatus = RequestStatus.Succeeded;
      state.integrations = state.integrations.map((integration) =>
        integration.id === action.payload.id
          ? { ...integration, ...action.payload }
          : integration,
      );
    });
    builder.addCase(rotateApiKeyRequest.rejected, (state, action) => {
      state.rotateApiKeyStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Update Field Mappings
    builder.addCase(updateFieldMappingsRequest.pending, (state) => {
      state.updateFieldMappingsStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(updateFieldMappingsRequest.fulfilled, (state, action) => {
      state.updateFieldMappingsStatus = RequestStatus.Succeeded;
      state.integrations = state.integrations.map((integration) =>
        integration.id === action.payload.id ? action.payload : integration,
      );
    });
    builder.addCase(updateFieldMappingsRequest.rejected, (state, action) => {
      state.updateFieldMappingsStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Delete Integration
    builder.addCase(deleteIntegrationRequest.pending, (state) => {
      state.deleteIntegrationStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(deleteIntegrationRequest.fulfilled, (state, action) => {
      state.deleteIntegrationStatus = RequestStatus.Succeeded;
      state.integrations = state.integrations.filter(
        (integration) => integration.id !== action.meta.arg,
      );
    });
    builder.addCase(deleteIntegrationRequest.rejected, (state, action) => {
      state.deleteIntegrationStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Verify Integration
    builder.addCase(verifyIntegrationRequest.pending, (state) => {
      state.verifyIntegrationStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(verifyIntegrationRequest.fulfilled, (state, action) => {
      state.verifyIntegrationStatus = RequestStatus.Succeeded;
      state.integrations = state.integrations.map((integration) =>
        integration.id === action.payload.id
          ? {
              ...integration,
              status: action.payload.status,
              lastVerifiedAt: action.payload.lastVerifiedAt,
            }
          : integration,
      );
    });
    builder.addCase(verifyIntegrationRequest.rejected, (state, action) => {
      state.verifyIntegrationStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Reconnect Integration
    builder.addCase(reconnectIntegrationRequest.pending, (state) => {
      state.reconnectIntegrationStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(reconnectIntegrationRequest.fulfilled, (state, action) => {
      state.reconnectIntegrationStatus = RequestStatus.Succeeded;
      state.integrations = state.integrations.map((integration) =>
        integration.id === action.payload.id
          ? { ...action.payload, user: action.payload.user ?? integration.user }
          : integration,
      );
    });
    builder.addCase(reconnectIntegrationRequest.rejected, (state, action) => {
      state.reconnectIntegrationStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Get Campaigns
    builder.addCase(getCampaignsRequest.pending, (state, action) => {
      state.getCampaignsStatus = {
        ...state.getCampaignsStatus,
        [action.meta.arg.integrationId]: RequestStatus.Pending,
      };
      state.error = null;
    });
    builder.addCase(getCampaignsRequest.fulfilled, (state, action) => {
      const { integrationId } = action.meta.arg;
      state.getCampaignsStatus = {
        ...state.getCampaignsStatus,
        [integrationId]: RequestStatus.Succeeded,
      };
      state.campaigns = {
        ...state.campaigns,
        [integrationId]: action.payload.campaigns,
      };
      state.cachedAt = {
        ...state.cachedAt,
        [integrationId]: action.payload.cachedAt,
      };
    });
    builder.addCase(getCampaignsRequest.rejected, (state, action) => {
      state.getCampaignsStatus = {
        ...state.getCampaignsStatus,
        [action.meta.arg.integrationId]: RequestStatus.Failed,
      };
      state.error = action.payload?.message || null;
    });

    // Get LinkedIn Accounts
    builder.addCase(getLinkedInAccountsRequest.pending, (state, action) => {
      state.getAccountsStatus = {
        ...state.getAccountsStatus,
        [action.meta.arg]: RequestStatus.Pending,
      };
      state.error = null;
    });
    builder.addCase(getLinkedInAccountsRequest.fulfilled, (state, action) => {
      state.getAccountsStatus = {
        ...state.getAccountsStatus,
        [action.meta.arg]: RequestStatus.Succeeded,
      };
      state.linkedInAccounts = {
        ...state.linkedInAccounts,
        [action.meta.arg]: action.payload,
      };
    });
    builder.addCase(getLinkedInAccountsRequest.rejected, (state, action) => {
      state.getAccountsStatus = {
        ...state.getAccountsStatus,
        [action.meta.arg]: RequestStatus.Failed,
      };
      state.error = action.payload?.message || null;
    });

    // Get Step Config
    builder.addCase(getStepConfigRequest.pending, (state) => {
      state.getStepConfigStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(getStepConfigRequest.fulfilled, (state, action) => {
      state.getStepConfigStatus = RequestStatus.Succeeded;
      state.stepConfigs = {
        ...state.stepConfigs,
        [action.payload.stepId]: action.payload,
      };
    });
    builder.addCase(getStepConfigRequest.rejected, (state, action) => {
      state.getStepConfigStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Retry LinkedIn Tasks
    builder.addCase(retryLinkedInTasksRequest.pending, (state) => {
      state.retryTasksStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(retryLinkedInTasksRequest.fulfilled, (state) => {
      state.retryTasksStatus = RequestStatus.Succeeded;
    });
    builder.addCase(retryLinkedInTasksRequest.rejected, (state, action) => {
      state.retryTasksStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Get Failed LinkedIn Tasks
    builder.addCase(getFailedLinkedInTasksRequest.pending, (state) => {
      state.getFailedTasksStatus = RequestStatus.Pending;
      state.error = null;
    });
    builder.addCase(getFailedLinkedInTasksRequest.fulfilled, (state, action) => {
      state.getFailedTasksStatus = RequestStatus.Succeeded;
      state.failedTasks = action.payload.tasks;
      state.failedTasksTotal = action.payload.total;
    });
    builder.addCase(getFailedLinkedInTasksRequest.rejected, (state, action) => {
      state.getFailedTasksStatus = RequestStatus.Failed;
      state.error = action.payload?.message || null;
    });

    // Get LinkedIn Tour Seen
    builder.addCase(getLinkedInTourSeenRequest.pending, (state) => {
      state.error = null;
    });
    builder.addCase(getLinkedInTourSeenRequest.fulfilled, (state, action) => {
      state.tourSeen = action.payload.seen;
    });
    builder.addCase(getLinkedInTourSeenRequest.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });

    // Dismiss LinkedIn Tour
    builder.addCase(dismissLinkedInTourRequest.pending, (state) => {
      state.error = null;
    });
    builder.addCase(dismissLinkedInTourRequest.fulfilled, (state) => {
      state.tourSeen = true;
    });
    builder.addCase(dismissLinkedInTourRequest.rejected, (state, action) => {
      state.error = action.payload?.message || null;
    });
  },
});

export const {
  resetConnectIntegrationStatus,
  resetRotateApiKeyStatus,
  resetUpdateFieldMappingsStatus,
  resetDeleteIntegrationStatus,
  resetVerifyIntegrationStatus,
  resetReconnectIntegrationStatus,
  resetRetryTasksStatus,
  setTourSeen,
} = linkedInAutomationSlice.actions;

export const linkedInAutomationReducer = linkedInAutomationSlice.reducer;
export default linkedInAutomationSlice.reducer;
